import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings as SettingsIcon,
  Description,
  Save,
  Cancel,
  Visibility,
  Category,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { documentsApi, unitTypeApi, UnitType } from '../services/api';
import BrokerManagement from '../components/BrokerManagement';

interface Document {
  id: number;
  title: string;
  content: string;
  type: 'agreement' | 'policy' | 'terms' | 'other';
  created_at: string;
  updated_at: string;
}

export const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [unitTypesLoading, setUnitTypesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [unitTypeDialogOpen, setUnitTypeDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [editingUnitType, setEditingUnitType] = useState<UnitType | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'agreement' as Document['type'],
  });
  const [unitTypeFormData, setUnitTypeFormData] = useState({
    name: '',
    label: '',
    description: '',
    is_active: true,
    sort_order: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user is admin or manager
  useEffect(() => {
    if (!user || !user.role || !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  // Load documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const documents = await documentsApi.getAll();
        setDocuments(documents);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
        setError('Failed to load documents');
        // Fallback to empty array if API fails
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchDocuments();
    }
  }, [user]);

  // Load unit types from API
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        setUnitTypesLoading(true);
        const unitTypes = await unitTypeApi.getAll();
        setUnitTypes(unitTypes);
      } catch (err) {
        console.error('Failed to fetch unit types:', err);
        setError('Failed to load unit types');
        setUnitTypes([]);
      } finally {
        setUnitTypesLoading(false);
      }
    };

    if (user && user.role && ['admin', 'manager'].includes(user.role)) {
      fetchUnitTypes();
    }
  }, [user]);

  const handleOpenDialog = (document?: Document) => {
    if (document) {
      setEditingDocument(document);
      setFormData({
        title: document.title,
        content: document.content,
        type: document.type,
      });
    } else {
      setEditingDocument(null);
      setFormData({
        title: '',
        content: '',
        type: 'agreement',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDocument(null);
    setFormData({
      title: '',
      content: '',
      type: 'agreement',
    });
    setError(null);
  };

  const handleViewDocument = (document: Document) => {
    setViewingDocument(document);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingDocument(null);
  };

  const handleSaveDocument = async () => {
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        setError('Title and content are required');
        return;
      }

      const documentData = {
        title: formData.title,
        content: formData.content,
        type: formData.type,
      };

      if (editingDocument) {
        // Update existing document
        const updatedDocument = await documentsApi.update(editingDocument.id, documentData);
        
        setDocuments(prev => 
          prev.map(doc => doc.id === editingDocument.id ? updatedDocument : doc)
        );
        setSuccess('Document updated successfully');
      } else {
        // Create new document
        const newDocument = await documentsApi.create(documentData);
        
        setDocuments(prev => [...prev, newDocument]);
        setSuccess('Document created successfully');
      }

      handleCloseDialog();
    } catch (err) {
      console.error('Failed to save document:', err);
      setError('Failed to save document');
    }
  };

  const handleDeleteDocument = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsApi.delete(id);
      
      setDocuments(prev => prev.filter(doc => doc.id !== id));
      setSuccess('Document deleted successfully');
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document');
    }
  };

  const getTypeColor = (type: Document['type']) => {
    switch (type) {
      case 'agreement': return 'primary';
      case 'policy': return 'secondary';
      case 'terms': return 'warning';
      default: return 'default';
    }
  };

  const getTypeLabel = (type: Document['type']) => {
    switch (type) {
      case 'agreement': return 'Agreement';
      case 'policy': return 'Policy';
      case 'terms': return 'Terms';
      default: return 'Other';
    }
  };

  // Unit Type Management Functions
  const handleOpenUnitTypeDialog = (unitType?: UnitType) => {
    if (unitType) {
      setEditingUnitType(unitType);
      setUnitTypeFormData({
        name: unitType.name,
        label: unitType.label,
        description: unitType.description || '',
        is_active: unitType.is_active,
        sort_order: unitType.sort_order,
      });
    } else {
      setEditingUnitType(null);
      const maxSortOrder = Math.max(...unitTypes.map(ut => ut.sort_order), 0);
      setUnitTypeFormData({
        name: '',
        label: '',
        description: '',
        is_active: true,
        sort_order: maxSortOrder + 1,
      });
    }
    setUnitTypeDialogOpen(true);
  };

  const handleCloseUnitTypeDialog = () => {
    setUnitTypeDialogOpen(false);
    setEditingUnitType(null);
    setUnitTypeFormData({
      name: '',
      label: '',
      description: '',
      is_active: true,
      sort_order: 0,
    });
    setError(null);
  };

  const handleSaveUnitType = async () => {
    try {
      if (!unitTypeFormData.name.trim() || !unitTypeFormData.label.trim()) {
        setError('Name and label are required');
        return;
      }

      // Validate name format (lowercase with underscores)
      const nameRegex = /^[a-z_]+$/;
      if (!nameRegex.test(unitTypeFormData.name)) {
        setError('Name must contain only lowercase letters and underscores');
        return;
      }

      const unitTypeData = {
        name: unitTypeFormData.name,
        label: unitTypeFormData.label,
        description: unitTypeFormData.description || undefined,
        is_active: unitTypeFormData.is_active,
        sort_order: unitTypeFormData.sort_order,
      };

      if (editingUnitType) {
        // Update existing unit type
        const updatedUnitType = await unitTypeApi.update(editingUnitType.id, unitTypeData);
        
        setUnitTypes(prev => 
          prev.map(ut => ut.id === editingUnitType.id ? updatedUnitType : ut)
        );
        setSuccess('Unit type updated successfully');
      } else {
        // Create new unit type
        const newUnitType = await unitTypeApi.create(unitTypeData);
        
        setUnitTypes(prev => [...prev, newUnitType]);
        setSuccess('Unit type created successfully');
      }

      handleCloseUnitTypeDialog();
    } catch (err: any) {
      console.error('Failed to save unit type:', err);
      setError(err.response?.data?.message || 'Failed to save unit type');
    }
  };

  const handleDeleteUnitType = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this unit type?')) {
      return;
    }

    try {
      await unitTypeApi.delete(id);
      
      setUnitTypes(prev => prev.filter(ut => ut.id !== id));
      setSuccess('Unit type deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete unit type:', err);
      setError(err.response?.data?.message || 'Failed to delete unit type');
    }
  };

  const handleReorderUnitType = async (id: number, direction: 'up' | 'down') => {
    const currentIndex = unitTypes.findIndex(ut => ut.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= unitTypes.length) return;

    const newUnitTypes = [...unitTypes];
    [newUnitTypes[currentIndex], newUnitTypes[newIndex]] = [newUnitTypes[newIndex], newUnitTypes[currentIndex]];

    // Update sort orders
    const reorderData = newUnitTypes.map((ut, index) => ({
      id: ut.id,
      sort_order: index,
    }));

    try {
      await unitTypeApi.reorder(reorderData);
      setUnitTypes(newUnitTypes.map((ut, index) => ({ ...ut, sort_order: index })));
      setSuccess('Unit types reordered successfully');
    } catch (err: any) {
      console.error('Failed to reorder unit types:', err);
      setError(err.response?.data?.message || 'Failed to reorder unit types');
    }
  };

  if (!user || !user.role || !['admin', 'manager'].includes(user.role)) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Settings
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Manage system documents and configurations
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Documents Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              Document Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage agreements, policies, and other legal documents
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Document
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Documents Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {document.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(document.type)}
                      color={getTypeColor(document.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {new Date(document.updated_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(document)}
                        color="info"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(document)}
                        color="primary"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDocument(document.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {documents.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
              No documents yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Start by creating your first document
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Create Document
            </Button>
          </Box>
        )}
      </Paper>

      {/* Unit Type Management Section */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              Unit Type Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage available unit types for properties
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenUnitTypeDialog()}
          >
            Add Unit Type
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Unit Types Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unitTypes.map((unitType, index) => (
                <TableRow key={unitType.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Category color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                        {unitType.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {unitType.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {unitType.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={unitType.is_active ? 'Active' : 'Inactive'}
                      color={unitType.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {unitType.sort_order}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleReorderUnitType(unitType.id, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleReorderUnitType(unitType.id, 'down')}
                          disabled={index === unitTypes.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenUnitTypeDialog(unitType)}
                        color="primary"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUnitType(unitType.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {unitTypes.length === 0 && !unitTypesLoading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Category sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
              No unit types yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Start by creating your first unit type
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenUnitTypeDialog()}
            >
              Create Unit Type
            </Button>
          </Box>
        )}
      </Paper>

      {/* Broker Management Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <BrokerManagement />
      </Paper>

      {/* Document Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingDocument ? 'Edit Document' : 'Create New Document'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Document Title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Document Type"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Document['type'] }))}
                  select
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="agreement">Agreement</option>
                  <option value="policy">Policy</option>
                  <option value="terms">Terms</option>
                  <option value="other">Other</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Document Content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  multiline
                  rows={12}
                  fullWidth
                  required
                  placeholder="Enter the document content here..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveDocument} 
            variant="contained"
            startIcon={<Save />}
          >
            {editingDocument ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Description color="primary" />
            <Box>
              <Typography variant="h6">
                {viewingDocument?.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {viewingDocument && getTypeLabel(viewingDocument.type)} • 
                Last updated: {viewingDocument && new Date(viewingDocument.updated_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Paper 
              sx={{ 
                p: 3, 
                backgroundColor: 'grey.50', 
                border: '1px solid',
                borderColor: 'grey.200',
                maxHeight: '400px',
                overflow: 'auto'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontFamily: 'monospace'
                }}
              >
                {viewingDocument?.content}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} startIcon={<Cancel />}>
            Close
          </Button>
          <Button 
            onClick={() => {
              handleCloseViewDialog();
              handleOpenDialog(viewingDocument || undefined);
            }}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit Document
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Type Dialog */}
      <Dialog 
        open={unitTypeDialogOpen} 
        onClose={handleCloseUnitTypeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUnitType ? 'Edit Unit Type' : 'Create New Unit Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Unit Type Name"
                  value={unitTypeFormData.name}
                  onChange={(e) => setUnitTypeFormData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  required
                  helperText="Lowercase letters and underscores only"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Unit Type Label"
                  value={unitTypeFormData.label}
                  onChange={(e) => setUnitTypeFormData(prev => ({ ...prev, label: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={unitTypeFormData.description}
                  onChange={(e) => setUnitTypeFormData(prev => ({ ...prev, description: e.target.value }))}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={unitTypeFormData.is_active}
                      onChange={(e) => setUnitTypeFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sort Order"
                  value={unitTypeFormData.sort_order}
                  onChange={(e) => setUnitTypeFormData(prev => ({ ...prev, sort_order: Number(e.target.value) }))}
                  type="number"
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnitTypeDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUnitType} 
            variant="contained"
            startIcon={<Save />}
          >
            {editingUnitType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
